/**
 * PricingCreatePage — Publication d'un nouveau tarif (POST /api/v1/admin/pricing).
 *
 * Route : /ops/pricing/new
 *
 * Le backend ferme automatiquement l'ancien tarif courant (si existant) pour
 * le tuple (provider, tier, unitType) et crée la nouvelle ligne.
 *
 * Accès : ROLE_PLATFORM_ADMIN.
 */

import { useForm } from '@refinedev/antd';
import type { BaseRecord, HttpError } from '@refinedev/core';
import { Button, DatePicker, Form, Input, InputNumber, Result, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { ArrowLeft, DollarSign, Save } from 'lucide-react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  PricingProvider,
  PricingTier,
  PublishPricingPayload,
  UnitType,
} from './types';
import {
  PRICING_PROVIDERS,
  PRICING_TIERS,
  PROVIDER_LABELS,
  TIER_LABELS,
  UNIT_TYPES,
  UNIT_TYPE_LABELS,
} from './types';

const PROVIDER_OPTIONS = PRICING_PROVIDERS.map((p) => ({
  value: p,
  label: PROVIDER_LABELS[p],
}));
const TIER_OPTIONS = PRICING_TIERS.map((t) => ({
  value: t,
  label: TIER_LABELS[t],
}));
const UNIT_OPTIONS = UNIT_TYPES.map((u) => ({
  value: u,
  label: UNIT_TYPE_LABELS[u],
}));

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

interface FormValues {
  provider: PricingProvider;
  tier: PricingTier;
  unitType: UnitType;
  priceCents: number;
  currency: string;
  effectiveFrom: Dayjs;
  description?: string;
}

const PricingCreatePage: FC = () => {
  const navigate = useNavigate();

  const { formProps, saveButtonProps, mutation } = useForm<
    BaseRecord,
    HttpError,
    FormValues
  >({
    resource: 'admin/pricing',
    action: 'create',
    redirect: false,
    onMutationSuccess: () => {
      navigate('/ops/pricing');
    },
  });

  const handleFinish = (values: FormValues) => {
    const payload: PublishPricingPayload = {
      provider: values.provider,
      tier: values.tier,
      unitType: values.unitType,
      priceCents: values.priceCents,
      currency: values.currency ?? 'USD',
      effectiveFrom: values.effectiveFrom.toISOString(),
      description: values.description?.trim() ? values.description.trim() : null,
    };
    // onFinish attend `FormValues` côté types mais le data-provider sérialise
    // l'objet tel quel. On force le type pour envoyer le payload backend.
    return formProps.onFinish?.(payload as unknown as FormValues);
  };

  return (
    <div className="space-y-6">
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Publier un tarif</h1>
            <p className="mt-1 text-sm text-blue-100">
              Crée une nouvelle tarification (le tarif courant est
              automatiquement clôturé)
            </p>
          </div>
        </div>
      </div>

      <div className="fc-card p-6">
        <Form<FormValues>
          {...formProps}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            currency: 'USD',
            effectiveFrom: dayjs(),
          }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Form.Item
              label="Provider"
              name="provider"
              rules={[{ required: true, message: 'Provider requis' }]}
            >
              <Select<PricingProvider>
                options={PROVIDER_OPTIONS}
                placeholder="Sélectionner un provider"
              />
            </Form.Item>
            <Form.Item
              label="Tier"
              name="tier"
              rules={[{ required: true, message: 'Tier requis' }]}
            >
              <Select<PricingTier>
                options={TIER_OPTIONS}
                placeholder="Sélectionner un tier"
              />
            </Form.Item>
            <Form.Item
              label="Unité"
              name="unitType"
              rules={[{ required: true, message: 'Unité requise' }]}
            >
              <Select<UnitType>
                options={UNIT_OPTIONS}
                placeholder="Sélectionner une unité"
              />
            </Form.Item>
            <Form.Item
              label="Prix (cents)"
              name="priceCents"
              tooltip="Prix en centièmes de la devise sélectionnée — ex: 300 = $3.00 ou €3.00"
              rules={[
                { required: true, message: 'Prix requis' },
                {
                  type: 'number',
                  min: 0,
                  message: 'Prix ne peut pas être négatif',
                },
              ]}
            >
              <InputNumber
                min={0}
                step={1}
                style={{ width: '100%' }}
                placeholder="ex. 300"
              />
            </Form.Item>
            <Form.Item
              label="Devise"
              name="currency"
              rules={[{ required: true, message: 'Devise requise' }]}
            >
              <Select options={CURRENCY_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="Effectif à partir de"
              name="effectiveFrom"
              rules={[{ required: true, message: 'Date requise' }]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
            <Form.Item
              label="Description (optionnel)"
              name="description"
              className="md:col-span-2"
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="Raison du changement, réf. source tarif officiel…"
              />
            </Form.Item>
          </div>

          {mutation?.isError && (
            <Result
              status="error"
              title="Erreur lors de la publication"
              subTitle={
                mutation.error instanceof Error
                  ? mutation.error.message
                  : 'Erreur inconnue'
              }
            />
          )}

          <div className="flex items-center justify-between">
            <Button
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/ops/pricing')}
            >
              Retour
            </Button>
            <Button
              {...saveButtonProps}
              type="primary"
              icon={<Save className="h-4 w-4" />}
            >
              Publier le tarif
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default PricingCreatePage;
